import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { useHasPermission } from "./session-context"
import { DownloadIcon, PlusIcon } from "./components/icons"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { Select } from "./components/select"
import { DonorPicker, type DonorOption } from "./components/donor-picker"
import { ChildPicker, type ChildOption } from "./components/child-picker"
import { GivingPageSkeleton } from "./components/skeleton"
import { ContentPagination } from "./content-components"
import { toast } from "sonner"

type DonationStatus = "pending" | "completed" | "refunded"
type DonationMethod = "card" | "bank_transfer" | "ussd"
type DonationDesignation = "scholarship" | "womens_program" | "general"
const PAGE_SIZE = 10

interface DonationListItem {
  id: string
  code: string
  receiptCode: string
  donorName: string
  donorEmail: string | null
  amount: number
  processorFee: number
  netAmount: number
  method: DonationMethod
  designation: DonationDesignation
  status: DonationStatus
  reference: string | null
  sponsorId: string | null
  childId: string | null
  schoolId: string | null
  donatedAt: string
}

interface SchoolOption {
  id: string
  label: string
  name: string
}

interface StatusCounts {
  all: number
  completed: number
  pending: number
  refunded: number
}

interface Summary {
  thisMonth: number
  thisQuarter: number
  thisYear: number
  averageGift: number
}

const STATUS_COLORS: Record<DonationStatus, { bg: string; text: string }> = {
  completed: { bg: "bg-green-100", text: "text-green-700" },
  pending: { bg: "bg-orange-100", text: "text-orange-600" },
  refunded: { bg: "bg-red-100", text: "text-red-700" },
}

const METHOD_LABELS: Record<DonationMethod, string> = {
  card: "Card",
  bank_transfer: "Bank transfer",
  ussd: "USSD",
}

const DESIGNATION_LABELS: Record<DonationDesignation, string> = {
  scholarship: "Scholarship",
  womens_program: "Women's program",
  general: "General",
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toISOString().split("T")[0] ?? dateString
}

function csvEscape(value: string | number | null): string {
  const text = value == null ? "" : String(value)
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

function buildDonationCsv(items: DonationListItem[]): string {
  const headers = [
    "Date",
    "Transaction ID",
    "Receipt",
    "Donor",
    "Donor Email",
    "Gross Amount",
    "Processor Fee",
    "Net Amount",
    "Method",
    "Designation",
    "Status",
    "Reference",
  ]
  const rows = items.map((donation) => [
    formatDate(donation.donatedAt),
    donation.code,
    donation.receiptCode,
    donation.donorName,
    donation.donorEmail,
    donation.amount,
    donation.processorFee,
    donation.netAmount,
    METHOD_LABELS[donation.method],
    DESIGNATION_LABELS[donation.designation],
    donation.status,
    donation.reference,
  ])
  return [headers, ...rows].map((row) => row.map((value) => csvEscape(value)).join(",")).join("\n")
}

interface FilterSelectProps {
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}

function FilterSelect({ value, options, onChange }: FilterSelectProps) {
  const selectedLabel = options.find((option) => option.value === value)?.label ?? ""

  return (
    <div className="w-36">
      <Select
        value={selectedLabel}
        onChange={(label) => onChange(options.find((option) => option.label === label)?.value ?? "")}
        options={options.map((option) => option.label)}
        placeholder="All"
      />
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-1 flex-col justify-between rounded-[10px] border border-gold-500 bg-white px-6 py-4 h-[140px]">
      <p className="text-sm font-semibold uppercase text-ink/50">{label}</p>
      <p className="font-display text-2xl font-bold text-ink sm:text-3xl">{value}</p>
    </div>
  )
}

export function GivingPage() {
  const navigate = useNavigate()
  const canView = useHasPermission("giving")
  const canMutate = useHasPermission("giving_mutate")
  const [donations, setDonations] = useState<DonationListItem[]>([])
  const [counts, setCounts] = useState<StatusCounts>({ all: 0, completed: 0, pending: 0, refunded: 0 })
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [summary, setSummary] = useState<Summary>({ thisMonth: 0, thisQuarter: 0, thisYear: 0, averageGift: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [methodFilter, setMethodFilter] = useState<string>("")
  const [designationFilter, setDesignationFilter] = useState<string>("")

  const fetchDonations = async () => {
    try {
      const query: Record<string, string> = {}
      query.limit = String(PAGE_SIZE)
      query.offset = String((page - 1) * PAGE_SIZE)
      if (statusFilter) query.status = statusFilter
      if (methodFilter) query.method = methodFilter
      if (designationFilter) query.designation = designationFilter

      const res = await client.api.admin.giving.$get({ query })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setDonations(body.data.items as DonationListItem[])
        setCounts(body.data.counts)
        setTotal(body.data.total)
        setSummary(body.data.summary)
      }
    } catch {
      setError("Failed to load donations")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchDonations()
    } else {
      setLoading(false)
    }
  }, [canView, statusFilter, methodFilter, designationFilter, page])

  useEffect(() => {
    setPage(1)
  }, [statusFilter, methodFilter, designationFilter])

  const handleExport = async () => {
    setExporting(true)
    try {
      const filters: Record<string, string> = {}
      if (statusFilter) filters.status = statusFilter
      if (methodFilter) filters.method = methodFilter
      if (designationFilter) filters.designation = designationFilter

      const firstResponse = await client.api.admin.giving.$get({
        query: { ...filters, limit: "100", offset: "0" },
      })
      const firstBody = await firstResponse.json()
      if (!firstResponse.ok || !("data" in firstBody)) {
        throw new Error(getErrorMessage(firstBody))
      }

      const items = [...(firstBody.data.items as DonationListItem[])]
      const totalItems = firstBody.data.total
      for (let offset = items.length; offset < totalItems; offset += 100) {
        const response = await client.api.admin.giving.$get({
          query: { ...filters, limit: "100", offset: String(offset) },
        })
        const body = await response.json()
        if (!response.ok || !("data" in body)) {
          throw new Error(getErrorMessage(body))
        }
        items.push(...(body.data.items as DonationListItem[]))
      }

      const blob = new Blob([buildDonationCsv(items)], { type: "text/csv;charset=utf-8" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `redempta-giving-${new Date().toISOString().split("T")[0]}.csv`
      link.click()
      URL.revokeObjectURL(url)
      toast.success(`Exported ${items.length} donation${items.length === 1 ? "" : "s"}`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to export donations")
    } finally {
      setExporting(false)
    }
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Giving</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view giving.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <GivingPageSkeleton />
  }

  const statusOptions = [
    { value: "", label: "All" },
    { value: "completed", label: "Completed" },
    { value: "pending", label: "Pending" },
    { value: "refunded", label: "Refunded" },
  ]

  const methodOptions = [
    { value: "", label: "All" },
    { value: "card", label: "Card" },
    { value: "bank_transfer", label: "Bank transfer" },
    { value: "ussd", label: "USSD" },
  ]

  const designationOptions = [
    { value: "", label: "All" },
    { value: "scholarship", label: "Scholarship" },
    { value: "womens_program", label: "Women's program" },
    { value: "general", label: "General" },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Giving</h1>
          <p className="mt-1 text-sm text-ink/60">Every donation flowing into the foundation.</p>
        </div>
        <div className="flex gap-3">
          {canMutate && (
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-forest-950 px-5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
            >
              <PlusIcon className="h-4 w-4" />
              Add Entry
            </button>
          )}
          <button
            onClick={() => void handleExport()}
            disabled={exporting}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-gold-500 bg-white px-5 text-sm font-medium text-ink transition-colors hover:bg-cream-100 disabled:opacity-50"
          >
            <DownloadIcon className="h-4 w-4" />
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <StatCard label="This Month" value={formatCurrency(summary.thisMonth)} />
        <StatCard label="This Quarter" value={formatCurrency(summary.thisQuarter)} />
        <StatCard label="This Year" value={formatCurrency(summary.thisYear)} />
        <StatCard label="Average Gift" value={formatCurrency(summary.averageGift)} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <FilterSelect
          value={designationFilter}
          options={designationOptions}
          onChange={setDesignationFilter}
        />
        <FilterSelect
          value={methodFilter}
          options={methodOptions}
          onChange={setMethodFilter}
        />
        <FilterSelect
          value={statusFilter}
          options={statusOptions}
          onChange={setStatusFilter}
        />
      </div>

      {/* Table */}
      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : donations.length === 0 ? (
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">No donations found</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gold-500/50">
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Date
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Donor
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Amount
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Method
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Designation
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold-500/30">
                {donations.map((donation) => (
                  <tr
                    key={donation.id}
                    onClick={() => navigate(`/admin/giving/${donation.id}`)}
                    className="cursor-pointer hover:bg-cream-50"
                  >
                    <td className="px-5 py-4 text-sm text-ink/50">{formatDate(donation.donatedAt)}</td>
                    <td className="px-5 py-4 text-sm text-ink">
                      <p>{donation.donorName}</p>
                      <p className="mt-0.5 text-xs text-ink/50">{donation.sponsorId ? "Sponsor-linked donor" : "Standalone donor"}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">{formatCurrency(donation.amount)}</td>
                    <td className="px-5 py-4 text-sm text-ink">{METHOD_LABELS[donation.method]}</td>
                    <td className="px-5 py-4 text-sm text-ink">{DESIGNATION_LABELS[donation.designation]}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                          STATUS_COLORS[donation.status].bg
                        } ${STATUS_COLORS[donation.status].text}`}
                      >
                        {donation.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ContentPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} disabled={loading} />

      {/* Add Donation Modal */}
      <AddDonationModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false)
          void fetchDonations()
        }}
      />
    </div>
  )
}

interface AddDonationModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

function AddDonationModal({ isOpen, onClose, onSuccess }: AddDonationModalProps) {
  const [saving, setSaving] = useState(false)
  const [donorName, setDonorName] = useState("")
  const [donorEmail, setDonorEmail] = useState("")
  const [amount, setAmount] = useState("")
  const [processorFeePercent, setProcessorFeePercent] = useState("1.5")
  const [method, setMethod] = useState<DonationMethod>("card")
  const [designation, setDesignation] = useState<DonationDesignation>("general")
  const [reference, setReference] = useState("")
  const [donatedAt, setDonatedAt] = useState(new Date().toISOString().split("T")[0] ?? "")
  const [children, setChildren] = useState<ChildOption[]>([])
  const [schools, setSchools] = useState<SchoolOption[]>([])
  const [donors, setDonors] = useState<DonorOption[]>([])
  const [childId, setChildId] = useState("")
  const [schoolId, setSchoolId] = useState("")
  const [donorId, setDonorId] = useState("")
  const [loadingLinks, setLoadingLinks] = useState(false)
  const [linksLoaded, setLinksLoaded] = useState(false)
  const [showCreateDonor, setShowCreateDonor] = useState(false)

  useEffect(() => {
    if (!isOpen || linksLoaded) return

    let cancelled = false
    setLoadingLinks(true)
    void Promise.all([
      client.api.admin.children.$get({ query: { limit: "100" } }),
      client.api.admin.schools.$get({ query: { limit: "100" } }),
      client.api.admin.donors.$get({ query: { limit: "100" } }),
    ])
      .then(async ([childrenRes, schoolsRes, donorsRes]) => {
        const [childrenBody, schoolsBody, donorsBody] = await Promise.all([
          childrenRes.json(),
          schoolsRes.json(),
          donorsRes.json(),
        ])
        if (cancelled) return
        if (childrenRes.ok && "data" in childrenBody) {
          setChildren(childrenBody.data.items as ChildOption[])
        }
        if (schoolsRes.ok && "data" in schoolsBody) {
          setSchools(
            schoolsBody.data.items.map((school) => ({
              id: school.id,
              label: `${school.code} · ${school.name}`,
              name: school.name,
            })),
          )
        }
        if (donorsRes.ok && "data" in donorsBody) {
          setDonors(donorsBody.data.items as DonorOption[])
        }
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load donation links")
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingLinks(false)
          setLinksLoaded(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, linksLoaded])

  const resetForm = () => {
    setDonorName("")
    setDonorEmail("")
    setAmount("")
    setProcessorFeePercent("1.5")
    setMethod("card")
    setDesignation("general")
    setReference("")
    setDonatedAt(new Date().toISOString().split("T")[0] ?? "")
    setDonorId("")
    setChildId("")
    setSchoolId("")
  }

  const handleSubmit = async () => {
    if (!donorId || !amount) {
      toast.error("Please select a donor and enter an amount")
      return
    }

    const amountNum = Number.parseFloat(amount)
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      toast.error("Please enter a valid amount")
      return
    }

    const feePercentNum = Number.parseFloat(processorFeePercent)
    if (Number.isNaN(feePercentNum) || feePercentNum < 0 || feePercentNum > 100) {
      toast.error("Please enter a processing fee between 0% and 100%")
      return
    }

    setSaving(true)
    try {
      const res = await client.api.admin.giving.$post({
        json: {
          donorId,
          amount: amountNum,
          processorFeePercent: feePercentNum,
          method,
          designation,
          childId: childId || undefined,
          schoolId: schoolId || undefined,
          reference: reference.trim() || undefined,
          donatedAt: donatedAt ? new Date(donatedAt).toISOString() : undefined,
        },
      })

      const body = await res.json()
      if (res.ok) {
        toast.success("Donation recorded successfully")
        resetForm()
        onSuccess()
      } else {
        toast.error(getErrorMessage(body))
      }
    } catch {
      toast.error("Failed to record donation")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Record Donation" maxWidth="lg">
      <div className="space-y-4">
        {showCreateDonor ? (
          <InlineCreateDonorForm
            onCancel={() => setShowCreateDonor(false)}
            onCreated={(donor) => {
              setDonors((current) => [...current, donor].sort((a, b) => a.name.localeCompare(b.name)))
              setDonorId(donor.id)
              setShowCreateDonor(false)
            }}
          />
        ) : (
          <DonorPicker
            donors={donors}
            value={donorId}
            onChange={setDonorId}
            onCreate={() => setShowCreateDonor(true)}
            disabled={loadingLinks}
          />
        )}

        {/* Amount & processing fee */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">
              Amount (NGN) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              min="1"
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Processing Fee (%)</label>
            <input
              type="number"
              value={processorFeePercent}
              onChange={(e) => setProcessorFeePercent(e.target.value)}
              min="0"
              max="100"
              step="0.01"
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
        </div>
        {Number.parseFloat(amount) > 0 && Number.parseFloat(processorFeePercent) >= 0 ? (
          <p className="-mt-2 rounded-lg bg-cream-100 px-3 py-2 text-xs text-ink/60">
            Processing fee: {formatCurrency(Math.round(Number.parseFloat(amount) * (Number.parseFloat(processorFeePercent) / 100)))}
            {" · "}
            Net to foundation: {formatCurrency(Number.parseFloat(amount) - Math.round(Number.parseFloat(amount) * (Number.parseFloat(processorFeePercent) / 100)))}
          </p>
        ) : null}

        {/* Child & School */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ChildPicker
            children={children}
            value={childId}
            onChange={setChildId}
            disabled={loadingLinks}
          />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Partner School</label>
            <Select
              value={schools.find((school) => school.id === schoolId)?.label ?? ""}
              onChange={(label) => setSchoolId(schools.find((school) => school.label === label)?.id ?? "")}
               options={schools.map((school) => school.label)}
               placeholder={loadingLinks ? "Loading schools..." : "Optional school link"}
               clearOption="Clear school selection"
               searchable
               searchPlaceholder="Search schools..."
               disabled={loadingLinks}
            />
          </div>
        </div>

        {/* Method & Designation */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Payment Method</label>
            <Select
              value={METHOD_LABELS[method]}
              onChange={(label) => {
                const entry = Object.entries(METHOD_LABELS).find(([, value]) => value === label)
                if (entry) setMethod(entry[0] as DonationMethod)
              }}
              options={Object.values(METHOD_LABELS)}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Designation</label>
            <Select
              value={DESIGNATION_LABELS[designation]}
              onChange={(label) => {
                const entry = Object.entries(DESIGNATION_LABELS).find(([, value]) => value === label)
                if (entry) setDesignation(entry[0] as DonationDesignation)
              }}
              options={Object.values(DESIGNATION_LABELS)}
            />
          </div>
        </div>

        {/* Reference */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Reference / Transaction ID</label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Optional reference number"
            className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>

        {/* Donation Date */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Donation Date</label>
          <input
            type="date"
            value={donatedAt}
            onChange={(e) => setDonatedAt(e.target.value)}
            className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>

        <ModalActions>
          <ModalButton onClick={onClose} disabled={saving}>
            Cancel
          </ModalButton>
          <ModalButton onClick={handleSubmit} variant="primary" disabled={saving}>
            {saving ? "Saving..." : "Record Donation"}
          </ModalButton>
        </ModalActions>
      </div>
    </Modal>
  )
}

function InlineCreateDonorForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (donor: DonorOption) => void
}) {
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const reset = () => {
    setName("")
    setEmail("")
    setPhone("")
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Donor name is required")
      return
    }
    setSaving(true)
    try {
      const response = await client.api.admin.donors.$post({
        json: {
          name: name.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
        },
      })
      const body = await response.json()
      if (!response.ok || !("data" in body)) {
        toast.error(getErrorMessage(body))
        return
      }
      reset()
      onCreated(body.data as DonorOption)
      toast.success("Donor created")
    } catch {
      toast.error("Failed to create donor")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="animate-fade-in-up rounded-lg border border-gold-500/60 bg-cream-50 p-4 shadow-sm transition-[border-color,background-color,box-shadow] duration-200 ease-out">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-forest-950">Create new donor</h3>
        <p className="mt-1 text-xs text-ink/60">Create a donor record before recording the gift.</p>
      </div>
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink">Name <span className="text-red-500">*</span></span>
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Full donor name" className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink">Email</span>
          <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="donor@example.com" className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink">Phone</span>
          <input value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="Optional phone number" className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none" />
        </label>
        <ModalActions>
          <ModalButton onClick={onCancel} disabled={saving}>Cancel</ModalButton>
          <ModalButton onClick={handleSubmit} variant="primary" disabled={saving}>{saving ? "Creating..." : "Create donor"}</ModalButton>
        </ModalActions>
      </div>
    </div>
  )
}
